import { getFormProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { Block, Button, f7, Icon, List, ListInput } from "framework7-react";
import { z as zod } from "zod";

import type { UpdateAccountBodyOneOf } from "@/generated/api";
import { useUpdateAccount } from "@/generated/api";
import { useRefetchCurrentUser } from "@/hooks/useRefetchCurrentUser";
import { useTokenStore } from "@/stores/tokenStore";

type ChangeEmailFormProps = {
  onCompleted: VoidFunction;
};

export function ChangeEmailForm({ onCompleted }: ChangeEmailFormProps) {
  const { setToken } = useTokenStore();

  const { refetch } = useRefetchCurrentUser();

  const { mutate, isPending } = useUpdateAccount();

  const emailSchema = zod.object({
    email: zod.string().email(),
  });

  const [form, fields] = useForm<UpdateAccountBodyOneOf>({
    defaultValue: {
      email: "",
    },
    onSubmit(event, { formData }) {
      event.preventDefault();
      const submission = parseWithZod(formData, { schema: emailSchema });

      if (submission.status !== "success") {
        return submission.reply();
      }

      mutate(
        { data: submission.value },
        {
          onError: () => {
            f7.toast.create({
              closeTimeout: 2000,
              text: "メールアドレスの更新に失敗しました: ",
            });
          },
          onSuccess: (response) => {
            if (response.status === 200) {
              const token = (response.headers["access-token"] as string | undefined) ?? "";
              const client = (response.headers["client"] as string | undefined) ?? "";
              const uid = (response.headers["uid"] as string | undefined) ?? "";
              setToken({ client, token, uid });
              refetch().finally(onCompleted);
            }
          },
        },
      );
    },
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: emailSchema });
    },
    shouldValidate: "onBlur",
  });

  return (
    <form {...getFormProps(form)}>
      <List>
        <ListInput
          clearButton
          floatingLabel
          required
          validate
          autocomplete="email"
          errorMessage={fields.email.errors?.join("\n")}
          info="メールアドレスを入力してください"
          label="メールアドレス"
          name={fields.email.name}
          placeholder="user@timefill.me"
          type="email"
        >
          <Icon
            material="email"
            slot="media"
          />
        </ListInput>
      </List>
      <Block>
        <Button
          fill
          large
          loading={isPending}
          type="submit"
        >
          メールアドレス更新
        </Button>
      </Block>
    </form>
  );
}
